import {
  type ShapeTextFrameHandle,
  getShapeTextFrame,
} from '@/components/app/shapes/shape-text-frame-registry'
import type { TextChain } from '@/api/types/stowed-element'
import { paragraphsToPlainText } from '@/lib/shapes/shape-text'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { type SpellText, leadingWordPiece, trailingWordPiece } from '@/lib/spell-check/spell-engine'
import { sortShapesInReadingOrder } from '../helpers/sort-shapes-in-reading-order'

type FrameLookup = (shapeId: string) => ShapeTextFrameHandle | undefined

interface CollectOptions {
  /** Looks up a mounted frame; defaults to the editor's frame registry. */
  getFrame?: FrameLookup
  /** Where the right page begins, or `null` for a single page. See `sortShapesInReadingOrder`. */
  spineX?: number | null
  /** The spread's text-link chains, so a word the flow cut between two frames is read whole. */
  chains?: readonly TextChain[]
}

/**
 * Marks where a chain's flow ran straight on from one frame into the next, in the middle of a
 * word: the first frame's text ends in a word piece and the next one's begins with one. The same
 * reading as the story engine, which fuses such a seam rather than assume the user pressed Enter
 * exactly at the frame's edge. A seam that ends or starts with a space, a hyphen or a line break is
 * left alone.
 */
function markWordsCutByChains(
  shapes: readonly ShapeData[],
  frameTexts: Map<string, SpellText>,
  chains: readonly TextChain[]
) {
  const shapeIdByUid = new Map(
    shapes.flatMap((shape) => (shape.uid === undefined ? [] : [[String(shape.uid), shape.id]]))
  )

  for (const { textBox } of chains) {
    for (let i = 0; i + 1 < textBox.length; i++) {
      const upstreamId = shapeIdByUid.get(String(textBox[i]!.uid))
      const downstreamId = shapeIdByUid.get(String(textBox[i + 1]!.uid))
      const upstream = upstreamId ? frameTexts.get(upstreamId) : undefined
      const downstream = downstreamId ? frameTexts.get(downstreamId) : undefined

      if (!upstream || !downstream) continue

      const piece = leadingWordPiece(downstream.text)

      if (!piece || !trailingWordPiece(upstream.text)) continue

      upstream.continuation = piece
      upstream.continuationShapeId = downstreamId
      downstream.skipLeading = piece.length
    }
  }
}

/**
 * The text of every text frame on the spread, one entry per frame, in reading order, plus what the
 * mounted editor knows about it. A mounted frame is the source of truth for its own text: it holds
 * the live buffer (ahead of the committed paragraphs while the user types), the caret, and how much
 * of the text the layout drew. A frame that isn't mounted falls back to its committed paragraphs.
 *
 * `overflowFrom` is set only while the frame is showing its overflow badge, so text a linked frame
 * hands on to the next one is not reported as hidden. Text set on a path is collected too, marked
 * `onPath`; it has no frame, caret or overflow.
 *
 * Shapes on a hidden layer are skipped: they are not drawn, so there is nowhere to underline a
 * word. This is about hidden layers only; overflow text inside a visible frame is still checked.
 */
export function collectShapeTexts(
  shapes: ShapeData[],
  { getFrame = getShapeTextFrame, spineX = null, chains = [] }: CollectOptions = {}
): SpellText[] {
  const visibleShapes = shapes.filter((shape) => !shape.hidden)
  const frameTexts = new Map<string, SpellText>()

  const collected = sortShapesInReadingOrder(visibleShapes, spineX).flatMap((shape) => {
    const texts: SpellText[] = []

    if (shape.paragraphs && shape.paragraphs.length > 0) {
      const frame = getFrame(shape.id)
      const frameText: SpellText = frame
        ? {
            shapeId: shape.id,
            text: frame.getPlainText(),
            caretOffset: frame.getEditingCaretOffset(),
            overflowFrom: frame.isShowingOverflowBadge() ? frame.getConsumedLength() : null,
          }
        : { shapeId: shape.id, text: paragraphsToPlainText(shape.paragraphs) }

      frameTexts.set(shape.id, frameText)
      texts.push(frameText)
    }

    if (shape.textOnPath?.text)
      texts.push({ shapeId: shape.id, text: shape.textOnPath.text, onPath: true })

    return texts
  })

  markWordsCutByChains(visibleShapes, frameTexts, chains)

  return collected
}
